import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { AuthProvider } from './hooks/useAuth'
import App from './App'
import './index.css'
import './i18n/config' // Initialize i18n
import { detectAndClearCorruptedCache, addCacheDebugInfo } from './utils/cacheUtils'
import { ErrorBoundary } from './components/ErrorBoundary'
import { LoadingTimeoutDetector } from './components/LoadingTimeoutDetector'
import { CookieConsent, isCookieConsentAccepted } from './components/CookieConsent'
import { NotificationProvider } from './components/ui/Notifications'
import { ThemeProvider } from './contexts/ThemeContext'
import { CompanyProvider } from './contexts/CompanyContext'

console.log('🚀 Sergio App Starting - Checking cache integrity...')
const hasCookieConsent = isCookieConsentAccepted()
let cacheWasCleared = false

if (hasCookieConsent) {
  cacheWasCleared = detectAndClearCorruptedCache()
  addCacheDebugInfo()

  if (cacheWasCleared) {
    console.log('🔄 Cache was cleared due to corruption or version mismatch')
  }
} else {
  console.log('ℹ️ Cookie consent not granted yet; deferring cache maintenance tasks')
}

// React Query Client - MEMORY-ONLY CACHE (No localStorage persistence)
// API responses are cached in memory with proper stale/gc times
// This provides fast navigation without bloating localStorage
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes - data considered fresh
      gcTime: 1000 * 60 * 30, // 30 minutes - cache retained in memory
      refetchOnMount: false, // Don't automatically refetch on mount
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnReconnect: true, // Only refetch when reconnecting
      retry: (failureCount, error: any) => {
        // Don't retry on authentication errors
        if (error?.status === 401 || error?.status === 403) return false
        // Don't retry on client errors (4xx)
        if (error?.status >= 400 && error?.status < 500) return false
        // Retry server errors (5xx) but with backoff
        return failureCount < 2
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
    },
    mutations: {
      retry: (failureCount, error: any) => {
        // Don't retry mutations on client errors
        if (error?.status >= 400 && error?.status < 500) return false
        // Retry server errors with limited attempts
        return failureCount < 1
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000)
    }
  }
})

// Force clear React Query cache on startup if cache was cleared
if (cacheWasCleared) {
  console.log('🧹 Clearing React Query cache due to cache reset')
  queryClient.clear()
}

if (!hasCookieConsent && typeof window !== 'undefined') {
  window.addEventListener('sergio-consent-granted', () => {
    const cleared = detectAndClearCorruptedCache()
    addCacheDebugInfo()
    if (cleared) {
      console.log('🔄 Cache cleared after consent grant')
      queryClient.clear()
    }
  }, { once: true })
}

// Add stable cache version
const CACHE_VERSION = '2025-09-22-stable'
console.log('🔄 React Query Cache Version:', CACHE_VERSION)

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <LoadingTimeoutDetector>
      <ErrorBoundary>
        <NotificationProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <CompanyProvider>
                <ThemeProvider>
                  <BrowserRouter>
                    <App />
                    <CookieConsent />
                  </BrowserRouter>
                </ThemeProvider>
              </CompanyProvider>
            </AuthProvider>
          </QueryClientProvider>
        </NotificationProvider>
      </ErrorBoundary>
    </LoadingTimeoutDetector>
  </React.StrictMode>
)
